"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import { Bell } from "lucide-react";
import { cn } from "../../lib/utils";
import { MenuPopover } from "../ui/MenuPopover";

/** Geteilter TopNav-Icon-Button-Stil (Theme-Toggle, Sync, Glocke identisch). */
export const TOPNAV_ICON_BTN =
  "p-2.5 rounded-[var(--radius)] text-muted-foreground/50 hover:text-foreground hover:bg-accent transition-colors disabled:hover:text-muted-foreground/50";

export interface NotificationItem {
  id: string;
  title: string;
  subtitle?: string;
  href: string;
}

export interface NotificationBellProps {
  unread: number;
  /** Gesetzt → Klick öffnet Popup mit dieser Liste. Sonst navigiert die Glocke zu `href`. */
  items?: NotificationItem[];
  /** href-Modus (ohne items): Ziel beim Klick. */
  href?: string;
  emptyLabel?: string;
  footerHref?: string;
  footerLabel?: string;
}

/** Badge-Text: kein Badge bei ≤0, Zahl bis 9, danach "9+". */
export function notificationBadgeText(unread: number): string | null {
  if (unread <= 0) return null;
  return unread > 9 ? "9+" : String(unread);
}

export function NotificationBell({
  unread, items, href,
  emptyLabel = "Keine ungelesenen Benachrichtigungen.",
  footerHref, footerLabel = "Alle Benachrichtigungen",
}: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const badge = notificationBadgeText(unread);
  const ariaLabel = unread > 0 ? `${unread} ungelesene Benachrichtigungen` : "Benachrichtigungen";

  const bellInner = (
    <>
      <Bell size={18} aria-hidden />
      {badge && (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
          {badge}
        </span>
      )}
    </>
  );

  // href-Modus: reine Navigation, kein Popup.
  if (!items) {
    return (
      <Link href={href ?? "#"} aria-label={ariaLabel} className={cn("relative", TOPNAV_ICON_BTN)}>
        {bellInner}
      </Link>
    );
  }

  // Popup-Modus.
  return (
    <div className="relative h-full flex items-center" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        aria-expanded={open}
        className={cn("relative", TOPNAV_ICON_BTN)}
      >
        {bellInner}
      </button>
      <MenuPopover
        open={open}
        onClose={() => setOpen(false)}
        anchor="top-right"
        anchorRef={ref}
        className="w-80 -right-2"
        // Glocke sitzt mittig im Header (nicht am rechten Rand) → nur Oberkante hart.
        roundedClassName="rounded-b-[calc(var(--radius)+2px)]"
      >
        <div className="px-3 py-2 text-xs font-semibold border-b border-border">Benachrichtigungen</div>
        {items.length === 0 ? (
          <div className="px-3 py-3 text-sm text-muted-foreground">{emptyLabel}</div>
        ) : (
          <div className="max-h-80 overflow-y-auto py-1">
            {items.map((it) => (
              <Link key={it.id} href={it.href} onClick={() => setOpen(false)} className="flex flex-col gap-0.5 px-3 py-2 hover:bg-accent transition-colors">
                <span className="w-full truncate text-sm font-medium">{it.title}</span>
                {it.subtitle && <span className="w-full truncate text-xs text-muted-foreground">{it.subtitle}</span>}
              </Link>
            ))}
          </div>
        )}
        {footerHref && (
          // `border-t border-border` bleibt hier stehen: die Kante trennt den
          // angehefteten Fuss vom scrollbaren Listenbereich darueber
          // (Trennlinie, KANTE_MIN 1,5 haelt sie), sie umrandet keine
          // Schaltflaeche. Anklickbar macht die Zeile Hover-Zustand + native
          // Link-Semantik — genau wie die Listenzeilen darueber, die GAR
          // keine Kante tragen (GUI-Audit 2026-09-19, Nebenmenge „Teilkanten").
          <Link href={footerHref} onClick={() => setOpen(false)} className="block border-t border-border px-3 py-2 text-center text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
            {footerLabel}
          </Link>
        )}
      </MenuPopover>
    </div>
  );
}
